import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  addBatchesAPI,
  updateBatchesAPI,
  getBatchesListAPI,
  getBatchesDetailAPI,
  deleteBatchesAPI,
  getUnassignedEmployeesAPI,
  addEmployeesToBatchAPI,
  removeEmployeesFromBatchAPI,
  getBatchesOptionsAPI,
  moveBatchEmployeesAPI,
} from "./batchApi";

// ✅ Add
export const addBatches = createAsyncThunk(
  "batches/add",
  async (data, { rejectWithValue }) => {
    try {
      return await addBatchesAPI(data);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to add Batch");
    }
  }
);

// ✅ Update
export const updateBatches = createAsyncThunk(
  "batches/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      return await updateBatchesAPI({ id, data });
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to update Batch");
    }
  }
);

// ✅ List
export const getBatchesList = createAsyncThunk(
  "batches/list",
  async (params, { rejectWithValue }) => {
    try {
      const { page = 1, limit = 10, search = "", company_id, excludeManagement, excludeOpenBatch } = params || {};
      return await getBatchesListAPI({ page, limit, search, company_id, excludeManagement, excludeOpenBatch });
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch Batch list");
    }
  }
);

// ✅ Detail — now accepts { id, page, limit, company_id }
export const getBatchesDetail = createAsyncThunk(
  "batches/detail",
  async ({ id, page = 1, limit = 10, company_id }, { rejectWithValue }) => {
    try {
      return await getBatchesDetailAPI(id, { page, limit, company_id });
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch Batch detail");
    }
  }
);

// ✅ Delete
export const deleteBatches = createAsyncThunk(
  "batches/delete",
  async ({ id, company_id }, { rejectWithValue }) => {
    try {
      return await deleteBatchesAPI(id, company_id);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to delete Batch");
    }
  }
);

// ✅ Get Unassigned Employees
export const getUnassignedEmployees = createAsyncThunk(
  "batches/unassignedEmployees",
  async ({ batchId, company_id }, { rejectWithValue }) => {
    try {
      return await getUnassignedEmployeesAPI(batchId, company_id);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch unassigned employees");
    }
  }
);

// ✅ Add Employees to Batch
export const addEmployeesToBatch = createAsyncThunk(
  "batches/addEmployees",
  async ({ batchId, employeeIds, company_id }, { rejectWithValue }) => {
    try {
      return await addEmployeesToBatchAPI({ batchId, employeeIds, company_id });
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to add employees to batch");
    }
  }
);

// ✅ Remove Employees from Batch
export const removeEmployeesFromBatch = createAsyncThunk(
  "batches/removeEmployees",
  async ({ batchId, employeeIds, company_id }, { rejectWithValue }) => {
    try {
      return await removeEmployeesFromBatchAPI({ batchId, employeeIds, company_id });
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to remove employees from batch");
    }
  }
);

// ✅ Get Batch Options (for dropdowns)
export const getBatchesOptions = createAsyncThunk(
  "batches/options",
  async (params, { rejectWithValue }) => {
    try {
      const { company_id, excludeManagement } = params || {};
      return await getBatchesOptionsAPI({ company_id, excludeManagement });
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch Batch options");
    }
  }
);

// ✅ Move Employees to Another Batch
export const moveBatchEmployees = createAsyncThunk(
  "batches/moveEmployees",
  async ({ targetBatchId, employeeIds, company_id }, { rejectWithValue }) => {
    try {
      return await moveBatchEmployeesAPI({ targetBatchId, employeeIds, company_id });
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to move employees to batch");
    }
  }
);

const initialState = {
  addBatches: { data: null, loading: false, error: null },
  updateBatches: { data: null, loading: false, error: null },
  getBatchesList: { data: [], total: 0, loading: false, error: null },
  getBatchesDetail: { data: null, total: 0, loading: false, error: null },
  deleteBatches: { data: null, loading: false, error: null },
  getUnassignedEmployees: { data: [], loading: false, error: null },
  addEmployeesToBatch: { data: null, loading: false, error: null },
  removeEmployeesFromBatch: { data: null, loading: false, error: null },
  getBatchesOptions: { data: [], loading: false, error: null },
  moveBatchEmployees: { data: null, loading: false, error: null },
};

const batchesSlice = createSlice({
  name: "batches",
  initialState,
  reducers: {
    clearAddBatches: (state) => { state.addBatches.data = null; state.addBatches.error = null; },
    clearUpdateBatches: (state) => { state.updateBatches.data = null; state.updateBatches.error = null; },
    clearBatchesDetail: (state) => { state.getBatchesDetail.data = null; state.getBatchesDetail.total = 0; state.getBatchesDetail.error = null; },
    clearDeleteBatches: (state) => { state.deleteBatches.data = null; state.deleteBatches.error = null; },
    clearUnassignedEmployees: (state) => { state.getUnassignedEmployees.data = []; state.getUnassignedEmployees.error = null; },
    clearAddEmployeesToBatch: (state) => { state.addEmployeesToBatch.data = null; state.addEmployeesToBatch.error = null; },
    clearRemoveEmployeesFromBatch: (state) => { state.removeEmployeesFromBatch.data = null; state.removeEmployeesFromBatch.error = null; },
    clearBatchesOptions: (state) => { state.getBatchesOptions.data = []; state.getBatchesOptions.error = null; },
    clearMoveBatchEmployees: (state) => { state.moveBatchEmployees.data = null; state.moveBatchEmployees.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addBatches.pending, (state) => { state.addBatches.loading = true; state.addBatches.error = null; })
      .addCase(addBatches.fulfilled, (state, action) => { state.addBatches.loading = false; state.addBatches.data = action.payload; })
      .addCase(addBatches.rejected, (state, action) => { state.addBatches.loading = false; state.addBatches.error = action.payload; })

      .addCase(updateBatches.pending, (state) => { state.updateBatches.loading = true; })
      .addCase(updateBatches.fulfilled, (state, action) => { state.updateBatches.loading = false; state.updateBatches.data = action.payload; })
      .addCase(updateBatches.rejected, (state, action) => { state.updateBatches.loading = false; state.updateBatches.error = action.payload; })

      .addCase(getBatchesList.pending, (state) => { state.getBatchesList.loading = true; })
      .addCase(getBatchesList.fulfilled, (state, action) => {
        state.getBatchesList.loading = false;
        state.getBatchesList.data = action.payload?.data || [];
        state.getBatchesList.total = action.payload?.total || action.payload?.count || (action.payload?.data ? action.payload.data.length : 0);
      })
      .addCase(getBatchesList.rejected, (state, action) => { state.getBatchesList.loading = false; state.getBatchesList.error = action.payload; })

      // ✅ Detail — now stores total for server-side pagination
      .addCase(getBatchesDetail.fulfilled, (state, action) => {
        state.getBatchesDetail.loading = false;
        state.getBatchesDetail.data = action.payload?.data || action.payload;
        // ✅ total lives inside pagination object
        state.getBatchesDetail.total = action.payload?.pagination?.total || 0;
      })
      .addCase(getBatchesDetail.rejected, (state, action) => { state.getBatchesDetail.loading = false; state.getBatchesDetail.error = action.payload; })

      .addCase(deleteBatches.pending, (state) => { state.deleteBatches.loading = true; state.deleteBatches.error = null; })
      .addCase(deleteBatches.fulfilled, (state, action) => { state.deleteBatches.loading = false; state.deleteBatches.data = action.payload?.data || action.payload; })
      .addCase(deleteBatches.rejected, (state, action) => { state.deleteBatches.loading = false; state.deleteBatches.error = action.payload; })

      .addCase(getUnassignedEmployees.pending, (state) => { state.getUnassignedEmployees.loading = true; state.getUnassignedEmployees.error = null; })
      .addCase(getUnassignedEmployees.fulfilled, (state, action) => { state.getUnassignedEmployees.loading = false; state.getUnassignedEmployees.data = action.payload?.data || action.payload || []; })
      .addCase(getUnassignedEmployees.rejected, (state, action) => { state.getUnassignedEmployees.loading = false; state.getUnassignedEmployees.error = action.payload; })

      .addCase(addEmployeesToBatch.pending, (state) => { state.addEmployeesToBatch.loading = true; state.addEmployeesToBatch.error = null; })
      .addCase(addEmployeesToBatch.fulfilled, (state, action) => { state.addEmployeesToBatch.loading = false; state.addEmployeesToBatch.data = action.payload; })
      .addCase(addEmployeesToBatch.rejected, (state, action) => { state.addEmployeesToBatch.loading = false; state.addEmployeesToBatch.error = action.payload; })

      .addCase(removeEmployeesFromBatch.pending, (state) => { state.removeEmployeesFromBatch.loading = true; state.removeEmployeesFromBatch.error = null; })
      .addCase(removeEmployeesFromBatch.fulfilled, (state, action) => { state.removeEmployeesFromBatch.loading = false; state.removeEmployeesFromBatch.data = action.payload; })
      .addCase(removeEmployeesFromBatch.rejected, (state, action) => { state.removeEmployeesFromBatch.loading = false; state.removeEmployeesFromBatch.error = action.payload; })

      .addCase(getBatchesOptions.pending, (state) => { state.getBatchesOptions.loading = true; state.getBatchesOptions.error = null; })
      .addCase(getBatchesOptions.fulfilled, (state, action) => { state.getBatchesOptions.loading = false; state.getBatchesOptions.data = action.payload?.data || action.payload || []; })
      .addCase(getBatchesOptions.rejected, (state, action) => { state.getBatchesOptions.loading = false; state.getBatchesOptions.error = action.payload; })

      .addCase(moveBatchEmployees.pending, (state) => { state.moveBatchEmployees.loading = true; state.moveBatchEmployees.error = null; })
      .addCase(moveBatchEmployees.fulfilled, (state, action) => { state.moveBatchEmployees.loading = false; state.moveBatchEmployees.data = action.payload; })
      .addCase(moveBatchEmployees.rejected, (state, action) => { state.moveBatchEmployees.loading = false; state.moveBatchEmployees.error = action.payload; });
  },
});

export const {
  clearAddBatches,
  clearUpdateBatches,
  clearBatchesDetail,
  clearDeleteBatches,
  clearUnassignedEmployees,
  clearAddEmployeesToBatch,
  clearRemoveEmployeesFromBatch,
  clearBatchesOptions,
  clearMoveBatchEmployees,
} = batchesSlice.actions;

export default batchesSlice.reducer;